/*
Branch + edge screens for the inspection-scheduling page (STREAM-38 v3).

- Wrong contact: landing link -> confirm (optional "who should we contact?"
  suggestion) -> POST -> terminal thanks. The terminal screen also renders on
  revisit when the GET payload says wrong_contact_reported.
- Escalation: "None of these times work" on the availability step -> free-text
  availability + preferred channel -> POST -> "we'll reach out shortly".
- Expired link: "Text me a new link" -> POST request-link, with sent and
  already-sent (429) states.

Copy note: no SLA promises ("shortly", never "1 business day") and no texting
promises on status copy — locked decisions on the v3 spec.
*/

import React, { useState } from 'react'
import { Input, Radio } from 'antd'

import { InfoCard, InfoRow, Screen, StatusPill } from './chrome'

const { TextArea } = Input

const lenderOr = lenderName => lenderName || 'your lender'

/* Wrong contact ------------------------------------------------------------ */

export const WrongContactConfirm = ({ lenderName, onConfirm, onBack, submitting, error }) => {
  const [suggestion, setSuggestion] = useState('')

  return (
    <Screen lenderName={lenderName} onBack={onBack} testId='wrong-contact-confirm'>
      <StatusPill tone='complete' label='Wrong contact' />
      <h1 className='isp-display isp-serif'>Not the right person?</h1>
      <p className='isp-lede'>
        No problem &mdash; confirm below and you won&rsquo;t be scheduled for this
        inspection. We&rsquo;ll let <b>{lenderOr(lenderName)}</b> know they need to
        reach someone else.
      </p>
      <label className='isp-field-label' htmlFor='wrong-contact-suggestion'>
        Know who we should contact? <span className='isp-optional'>Optional</span>
      </label>
      <TextArea
        id='wrong-contact-suggestion'
        className='isp-textarea'
        rows={3}
        maxLength={1000}
        placeholder='Name and phone or email — only if you&rsquo;d like to help'
        value={suggestion}
        onChange={e => setSuggestion(e.target.value)}
      />
      {error && <div className='isp-error'>{error}</div>}
      <div className='isp-cta-stack'>
        <button
          className='isp-cta'
          data-testid='wrong-contact-submit'
          disabled={submitting}
          onClick={() => onConfirm(suggestion.trim())}
        >
          {submitting ? 'Sending…' : 'Confirm — this isn’t me'}
        </button>
      </div>
    </Screen>
  )
}

export const WrongContactDone = ({ lenderName }) => (
  <Screen lenderName={lenderName} testId='wrong-contact-done'>
    <StatusPill tone='complete' label='Wrong contact' />
    <h1 className='isp-display isp-serif'>Thanks &mdash; we&rsquo;ll sort this out</h1>
    <p className='isp-lede'>
      You won&rsquo;t be scheduled for this inspection. We&rsquo;ve
      let <b>{lenderOr(lenderName)}</b> know they&rsquo;ll need to reach the right
      person. You can close this page.
    </p>
  </Screen>
)

/* Escalation ---------------------------------------------------------------- */

const CHANNEL_OPTIONS = [
  { value: 'text', label: 'Text me' },
  { value: 'call', label: 'Call me' },
  { value: 'email', label: 'Email me' },
]

export const channelLabel = channel => {
  const option = CHANNEL_OPTIONS.find(o => o.value === channel)
  return option ? option.label.replace('me', 'you') : null
}

export const EscalationForm = ({ lenderName, onSubmit, onBack, submitting, error }) => {
  const [availabilityText, setAvailabilityText] = useState('')
  const [channel, setChannel] = useState('text')

  return (
    <Screen lenderName={lenderName} onBack={onBack} testId='escalation-form'>
      <StatusPill tone='action' label='Action needed' />
      <h1 className='isp-q'>Let&rsquo;s find a time that works</h1>
      <p className='isp-lede'>
        No problem &mdash; tell us when you&rsquo;re usually free and
        how <b>{lenderOr(lenderName)}</b> can reach you.
      </p>
      <label className='isp-field-label' htmlFor='escalation-availability'>When are you usually free?</label>
      <TextArea
        id='escalation-availability'
        className='isp-textarea'
        rows={3}
        maxLength={2000}
        placeholder='Weekday evenings after 5, weekends anytime…'
        value={availabilityText}
        onChange={e => setAvailabilityText(e.target.value)}
      />
      <div className='isp-field-label'>Best way to reach you</div>
      <Radio.Group
        className='isp-radio-stack'
        value={channel}
        onChange={e => setChannel(e.target.value)}
      >
        {CHANNEL_OPTIONS.map(option => (
          <Radio key={option.value} value={option.value} className='isp-radio'>
            {option.label}
          </Radio>
        ))}
      </Radio.Group>
      {error && <div className='isp-error'>{error}</div>}
      <div className='isp-cta-stack'>
        <button
          className='isp-cta'
          data-testid='escalation-submit'
          disabled={submitting || !availabilityText.trim()}
          onClick={() => onSubmit({ availabilityText: availabilityText.trim(), preferredChannel: channel })}
        >
          {submitting ? 'Sending…' : 'Send this to ' + lenderOr(lenderName)}
        </button>
      </div>
    </Screen>
  )
}

// detail is { availabilityText, preferredChannel } right after an in-session
// submit; on revisit (escalation_requested from the GET payload) we only know
// the request happened, so the recap card slims down to the property row.
export const EscalationDone = ({ page, lenderName, detail }) => {
  const address = page.order && page.order.address
  const reach = detail && channelLabel(detail.preferredChannel)

  return (
    <Screen lenderName={lenderName} testId='escalation-done'>
      <StatusPill tone='reggora' label='We’ll reach out' />
      <h1 className='isp-display isp-serif'>We&rsquo;ll reach out to you</h1>
      <p className='isp-lede'>
        Thanks &mdash; someone will reach out shortly to find a time that works.
        No further action is needed right now.
      </p>
      <InfoCard>
        {address && <InfoRow label='Property'>{address}</InfoRow>}
        {detail && detail.availabilityText && (
          <InfoRow label='You told us'>{detail.availabilityText}</InfoRow>
        )}
        {reach && <InfoRow label='We’ll'>{reach}</InfoRow>}
      </InfoCard>
    </Screen>
  )
}

/* Expired link --------------------------------------------------------------- */

export const ExpiredScreen = ({ lenderName, onRequestLink }) => {
  // idle -> sending -> sent | limited | error(back to idle with message)
  const [state, setState] = useState('idle')
  const [error, setError] = useState(null)

  const request = async () => {
    setState('sending')
    setError(null)
    const result = await onRequestLink()
    if (!result || !result.error) {
      setState('sent')
    } else if (result.status === 429) {
      setState('limited')
    } else {
      setState('idle')
      setError(result.error)
    }
  }

  if (state === 'sent') {
    return (
      <Screen lenderName={lenderName} testId='expired-link-sent'>
        <StatusPill tone='reggora' label='Link sent' />
        <h1 className='isp-display isp-serif'>Check your messages</h1>
        <p className='isp-lede'>
          We just texted you a fresh scheduling link. Open it to pick up where
          you left off &mdash; you can close this page.
        </p>
      </Screen>
    )
  }

  if (state === 'limited') {
    return (
      <Screen lenderName={lenderName} testId='expired-link-limited'>
        <StatusPill tone='reggora' label='Link sent' />
        <h1 className='isp-display isp-serif'>A link is already on its way</h1>
        <p className='isp-lede'>
          We recently sent you a new scheduling link &mdash; check your messages.
          If it hasn&rsquo;t arrived, contact support below.
        </p>
      </Screen>
    )
  }

  return (
    <Screen lenderName={lenderName} testId='expired-link'>
      <StatusPill tone='complete' label='Link expired' />
      <h1 className='isp-display isp-serif'>This link has expired</h1>
      <p className='isp-lede'>
        For your security, scheduling links stop working after a while. If you
        still need to schedule, request a fresh one.
      </p>
      {error && <div className='isp-error'>{error}</div>}
      <div className='isp-cta-stack'>
        <button
          className='isp-cta'
          data-testid='request-new-link'
          disabled={state === 'sending'}
          onClick={request}
        >
          {state === 'sending' ? 'Sending…' : 'Text me a new link'}
        </button>
      </div>
    </Screen>
  )
}
